import styled from "@emotion/styled";
import UI from "@mile-meeting/mile-ui";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import React, { useEffect } from "react";
import { getKorDate } from "utils/date";
import { set } from "lodash";

type RoomUsageHeatmapProps = {
  filterStartDate?: Date;
  filterEndDate?: Date;
  data: {
    date: string;
    meetingTime: number;
  }[];
  maxMeetingTime?: number;
};

const RoomUsageHeatmap: React.FC<RoomUsageHeatmapProps> = ({
  filterEndDate = new Date(),
  filterStartDate = new Date(),
  data,
  maxMeetingTime,
}) => {
  const days = ["일", "월", "화", "수", "목", "금", "토"];
  const globalStyle = getComputedStyle(document.documentElement);
  const [viewStartDate, setViewStartDate] = React.useState<Date>(new Date());
  const viewEndDate = new Date(viewStartDate.getFullYear(), viewStartDate.getMonth() + 1, 0);

  const getSeriesData = (start: Date, end: Date) => {
    const meetingMap = new Map(data.map((item) => [new Date(item.date).toISOString().slice(0, 10), item.meetingTime]));

    const series: { name: string; data: { x: string; y: number; meta: string }[] }[] = [];

    const startOfMonth = new Date(viewStartDate.getFullYear(), viewStartDate.getMonth(), 1);
    const startOffset = startOfMonth.getDay(); // 0: 일요일

    // ✅ 시작일: 해당 월의 1일이 포함된 주의 일요일
    const startDate = new Date(startOfMonth);
    startDate.setDate(startOfMonth.getDate() - startOffset);

    const totalDays = 6 * 7; // ✅ 항상 6주로 고정

    const weekDataArray: { x: string; y: number; meta: string }[][] = [];

    let current = new Date(startDate);
    for (let i = 0; i < totalDays; i++) {
      const weekIndex = Math.floor(i / 7);
      const iso = current.toISOString().slice(0, 10);
      const day = current.getDay();

      const y = current.getMonth() === viewStartDate.getMonth() ? meetingMap.get(iso) ?? 0 : -1;
      const dayData = {
        x: days[day],
        y,
        meta: `${current.getMonth() + 1}월 ${current.getDate()}일`,
      };

      if (!weekDataArray[weekIndex]) {
        weekDataArray[weekIndex] = [];
      }

      weekDataArray[weekIndex].push(dayData);

      current.setDate(current.getDate() + 1);
    }

    // ✅ 역순 정렬 (W6 → W1)
    const reversed = weekDataArray.reverse();
    reversed.forEach((week, idx) => {
      series.push({
        name: `W${reversed.length - idx}`,
        data: week,
      });
    });

    return series;
  };

  let maxTime: number = maxMeetingTime ?? 0;
  let heatmapColors: string[] = [
    globalStyle.getPropertyValue("--global-color-skyblue-95"),
    globalStyle.getPropertyValue("--global-color-skyblue-90"),
    globalStyle.getPropertyValue("--global-color-skyblue-80"),
    globalStyle.getPropertyValue("--global-color-skyblue-70"),
    globalStyle.getPropertyValue("--global-color-skyblue-60"),
    globalStyle.getPropertyValue("--global-color-skyblue-50"),
    globalStyle.getPropertyValue("--global-color-skyblue-40"),
  ];
  let colorScaleRanges: object[] = [
    {
      from: -1,
      to: -1,
      color: "#FFFFFF",
    },
  ];
  if (maxTime > 0) {
    const step = maxTime / heatmapColors.length;

    for (let i = 0; i < heatmapColors.length; i++) {
      const from = parseFloat((i * step).toFixed(4));
      const to =
        i === heatmapColors.length - 1
          ? maxTime // 마지막 구간은 maxTime까지 포함
          : parseFloat(((i + 1) * step - 0.0001).toFixed(4)); // 겹치지 않게 살짝 줄임

      colorScaleRanges.push({
        from,
        to,
        color: heatmapColors[i],
      });
    }
  } else {
    colorScaleRanges.push({
      from: 0,
      to: 0,
      color: heatmapColors[0],
    });
  }

  const handlePrevMonth = () => {
    const startDate = new Date(viewStartDate.getFullYear(), viewStartDate.getMonth() - 1, 1);
    const endDate = new Date(viewStartDate.getFullYear(), viewStartDate.getMonth(), -1);

    if (endDate < filterStartDate) return;

    //* 필터건 날짜를 초과할 경우 페이지네이션 불가능
    setViewStartDate(startDate);
  };

  const handleNextMonth = () => {
    const startDate = new Date(viewStartDate.getFullYear(), viewStartDate.getMonth() + 1, 1);

    //* 필터건 날짜를 초과할 경우 페이지네이션 불가능
    if (startDate > filterEndDate) return;

    setViewStartDate(startDate);
  };

  useEffect(() => {
    //* INFO: 데이터가 있을 경우, 해당 데이터의 첫 번째 날짜를 기준으로 시작 날짜를 설정
    let startDate = new Date(viewStartDate.getFullYear(), viewStartDate.getMonth(), 1);
    if (data.length > 0) startDate = new Date(filterStartDate.getFullYear(), filterStartDate.getMonth(), 1);
    setViewStartDate(startDate);
  }, [data]);

  const options: ApexOptions = {
    chart: {
      type: "heatmap",
      height: 300,
      offsetY: 20,
      background: "#FFFFFF",
      zoom: {
        enabled: false,
      },
      animations: {
        enabled: false, // 창 크기 변경에 의한 리렌더링 비활성화
      },
    },

    dataLabels: {
      enabled: false,
    },
    yaxis: {
      show: false,
    },
    legend: {
      show: false,
    },
    grid: {
      padding: {
        right: 1,
        bottom: -26,
        left: 1,
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
    },
    xaxis: {
      type: "category",
      position: "top",
      labels: {
        show: true,
        hideOverlappingLabels: false,
        style: {
          colors: "#A0A8B0",
          fontSize: "10px",
          fontWeight: 500,
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    stroke: {
      show: true,
      lineCap: "round",
      colors: ["#FFFFFF"],
      width: 6,
      dashArray: 0,
    },
    colors: heatmapColors,
    plotOptions: {
      heatmap: {
        radius: 10,
        shadeIntensity: 0.55,
        useFillColorAsStroke: false,
        colorScale: {
          ranges: colorScaleRanges,
        },
      },
    },
    states: {
      active: {
        filter: {
          type: "none", // ✅ 클릭 시 오버레이 효과 제거
        },
      },
    },
    tooltip: {
      custom: ({ seriesIndex, dataPointIndex, w }) => {
        const meta = w.globals.initialSeries[seriesIndex].data[dataPointIndex].meta;
        const y = w.globals.initialSeries[seriesIndex].data[dataPointIndex].y;

        if (y === -1) return "";
        else
          return `
          <div style="
              padding: 12px;
              font-size: 12px;
              font-weight: 500;
              display: flex;
              flex-direction: column;
              gap: 8px;">
            <div>${meta}</div>
            <div>회의 시간: ${y}</div>
          </div>
        `;
      },
    },
  };

  return (
    <Container>
      <TopSection>
        <UI.Typography type="body01 normal" fontStyle="bold" color="var(--alias-color-label-neutral)">
          회의실 이용 현황
        </UI.Typography>
        <UI.Typography
          type="caption01"
          color="var(--alias-color-label-alternative)"
          style={{
            marginLeft: "auto",
          }}
        >
          {viewStartDate.getFullYear()}년 {(viewStartDate.getMonth() + 1).toString().padStart(2, "0")}월
          {/* {getKorDate(viewStartDate.toDateString()).replaceAll(".", ". ")} -{" "}
          {getKorDate(new Date(viewStartDate.getFullYear(), viewStartDate.getMonth() + 1, 0).toDateString()).replaceAll(
            ".",
            ". "
          )} */}
        </UI.Typography>
        <ArrowBtnContainer>
          <UI.IconButton.Normal type="secondary" icon="arrowLeft" size={16} onClick={handlePrevMonth} />
          <UI.IconButton.Normal type="secondary" icon="arrowRight" size={16} onClick={handleNextMonth} />
        </ArrowBtnContainer>
      </TopSection>
      <Chart
        options={options}
        series={getSeriesData(viewStartDate, viewEndDate) ?? []}
        type="heatmap"
        width="320"
        height={300}
      />
    </Container>
  );
};

export default RoomUsageHeatmap;

const Container = styled.div`
  display: flex;
  width: 360px;
  height: 480px;
  padding: 20px;
  flex-direction: column;
  align-items: flex-start;
  /* gap: 20px; */
  box-sizing: border-box;

  border-radius: 12px;
  border: 1px solid var(--alias-color-line-normal);
  background: var(--alias-color-background-normal-normal);
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const ArrowBtnContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;
